import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { track } from '../lib/analytics';
import { Field } from '../components/ui';
import { useTheme } from '../lib/theme';
import logoDark from '../assets/proactive-logo-dark.png';
import logoWhite from '../assets/proactive-logo-white.png';

/** A dónde volver tras el login: la página donde estaba el usuario cuando su sesión expiró
 *  (la guarda el cliente API al recibir un 401). Solo rutas internas — nada de open redirect. */
function postLoginPath(): string {
  try {
    const back = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    if (back && back.startsWith('/') && !back.startsWith('//') && !back.includes('/login')) return back;
  } catch { /* */ }
  return '/';
}

export function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { t } = useI18n();
  const { theme } = useTheme();
  // El slug ya NO se pide (Manuel 2026-09-30: confundía). Solo aparece un selector si el backend
  // responde company_required (mismo email y clave en más de una empresa).
  const [companySlug, setSlug] = useState('');
  const [companies, setCompanies] = useState<Array<{ slug: string; name: string }> | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Segundo paso (2FA): si el backend responde mfaRequired guardamos el token.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  function apiErrorMessage(err: unknown): string {
    if (err instanceof ApiError && err.status === 429) {
      const mins = Math.max(1, Math.ceil((err.retryAfterSec ?? 0) / 60));
      return err.code === 'account_locked' ? t('auth.locked', { mins }) : t('auth.throttled');
    }
    if (err instanceof ApiError && err.status === 401) return t('auth.invalidCreds');
    return t('auth.error');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{ kind: string; mfaRequired?: boolean; mfaToken?: string }>(
        '/api/auth/login',
        { ...(companySlug ? { companySlug } : {}), email, password },
      );
      if (res.mfaRequired && res.mfaToken) {
        setMfaToken(res.mfaToken);
        return; // muestra el paso del código
      }
      track('login', { method: 'password' });
      await refresh();
      nav(postLoginPath());
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'company_required') {
        const lista = ((err.body as { companies?: Array<{ slug: string; name: string }> } | undefined)?.companies) ?? [];
        setCompanies(lista); setSlug(lista[0]?.slug ?? '');
        return;
      }
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/login/mfa', { mfaToken, code: code.trim() });
      track('login', { method: '2fa' });
      await refresh();
      nav(postLoginPath());
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'mfa_expired' || err.status === 401 && err.code === 'mfa_expired')) {
        setError(t('auth.mfaExpired'));
        setMfaToken(null);
        setCode('');
      } else if (err instanceof ApiError && err.status === 401) {
        setError(t('auth.mfaInvalid'));
      } else {
        setError(apiErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap auth-split">
      {/* Panel de marca (mockup B-login): oscuro en AMBOS temas, glow índigo + grilla sutil.
          En pantallas angostas desaparece y queda solo el form. */}
      <aside className="auth-brandpanel">
        <img src={logoWhite} alt="Proactive" className="auth-bp-logo" />
        <div className="auth-bp-mid">
          <div className="auth-bp-title">{t('auth.brandTitle')}</div>
          <div className="auth-bp-sub">{t('auth.brandSub')}</div>
        </div>
        <div />
      </aside>
      <div className="auth-side">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={theme === 'dark' ? logoWhite : logoDark} alt="Proactive" className="auth-logo" />
        </div>
        {!mfaToken ? (
          <>
            <div className="auth-title">{t('auth.signin')}</div>
            <div className="auth-sub">{t('auth.signinSub')}</div>
            <form onSubmit={submit}>
              {companies && companies.length > 0 && (
                <label className="field">
                  {t('auth.company')}
                  <select className="input" value={companySlug} onChange={(e) => setSlug(e.target.value)}>
                    {companies.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('auth.pickCompany')}</span>
                </label>
              )}
              <Field label={t('auth.email')} type="email" value={email} onChange={setEmail} />
              <Field label={t('auth.password')} type="password" value={password} onChange={setPassword} />
              {error && <div className="error">{error}</div>}
              <button className="btn" style={{ width: '100%' }} disabled={busy}>
                {busy ? '…' : t('auth.enter')}
              </button>
            </form>
            <p style={{ marginTop: 16, fontSize: 'var(--fs-sm)' }} className="muted">
              <Link to="/platform/login">{t('auth.platformQ')}</Link>
            </p>
          </>
        ) : (
          <>
            <div className="auth-title">{t('auth.mfaTitle')}</div>
            <div className="auth-sub">{t('auth.mfaSub')}</div>
            <form onSubmit={submitMfa}>
              <Field
                label={t('auth.mfaCode')}
                value={code}
                onChange={(v) => setCode(v.replace(/[^0-9A-Za-z-]/g, ''))}
                placeholder="123456"
              />
              {error && <div className="error">{error}</div>}
              <button className="btn" style={{ width: '100%' }} disabled={busy || code.trim().length < 6}>
                {busy ? '…' : t('auth.mfaVerify')}
              </button>
            </form>
            <p style={{ marginTop: 16, fontSize: 'var(--fs-sm)' }} className="muted">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setMfaToken(null);
                  setCode('');
                  setError(null);
                }}
              >
                {t('auth.mfaBack')}
              </a>
            </p>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
